import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useSEO } from "../lib/seo";
import { SoftwareCard } from "../components/SoftwareCard";

export default function SearchResults() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [results, setResults] = useState(null);

  useSEO({ title: `Search: ${q}`, description: `Search results for ${q}`, noindex: true });

  useEffect(() => {
    if (q) api.get("/search", { params: { q } }).then(({ data }) => setResults(data));
  }, [q]);

  if (!results) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-slate-400">Searching…</div>;

  const empty = !results.software.length && !results.articles.length && !results.comparisons.length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16" data-testid="search-results-page">
      <h1 className="font-heading text-4xl font-black tracking-tight text-slate-900">
        Results for "<span className="text-sky-500">{q}</span>"
      </h1>
      {empty && <p className="mt-10 text-slate-400" data-testid="search-empty">Nothing found. Try a different term.</p>}

      {results.software.length > 0 && (
        <section className="mt-12">
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-5">Software</h2>
          <div className="space-y-4">{results.software.map((s) => <SoftwareCard key={s.slug} software={s} />)}</div>
        </section>
      )}
      {results.comparisons.length > 0 && (
        <section className="mt-12">
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-5">Comparisons</h2>
          <div className="space-y-3">
            {results.comparisons.map((c) => (
              <Link key={c.slug} to={`/compare/${c.slug}`} className="block bg-white rounded-2xl border border-border p-5 hover:border-sky-300 transition-colors duration-200">
                <h3 className="font-heading font-semibold text-slate-900">{c.title}</h3>
                <p className="text-sm text-slate-500 line-clamp-1 mt-1">{c.intro}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
      {results.articles.length > 0 && (
        <section className="mt-12">
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-5">Guides</h2>
          <div className="space-y-3">
            {results.articles.map((a) => (
              <Link key={a.slug} to={`/guides/${a.slug}`} className="block bg-white rounded-2xl border border-border p-5 hover:border-sky-300 transition-colors duration-200">
                <h3 className="font-heading font-semibold text-slate-900">{a.title}</h3>
                <p className="text-sm text-slate-500 line-clamp-1 mt-1">{a.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
